<template>
  <div :class="containerClass">

    <h2>Add a Letter of Recommendation</h2>

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Type*" label-for="type" label-class="form-label">
        <v-select
          id="type"
          class="form-control v-select-custom"
          :class="!$v.type.required && $v.type.$dirty ? 'form-control is-invalid' : ''"
          v-model="type"
          :options="recommendationTypes"
          placeholder="Select a type"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!type" v-bind="attributes" v-on="events" />
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.type.required && $v.type.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group v-if="type === 'Other'" label="Other" label-for="typeOther" label-class="form-label">
        <b-form-input
          id="typeOther"
          placeholder="Enter type"
          v-model="typeOther"
          :state="!$v.typeOther.required && $v.typeOther.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Recommender Name*" label-for="recommenderName" label-class="form-label">
        <b-form-input
          id="recommenderName"
          placeholder="Enter recommender name"
          v-model="recommenderName"
          :state="!$v.recommenderName.required && $v.recommenderName.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Recommender Title" label-for="recommenderTitle" label-class="form-label">
        <b-form-input id="recommenderTitle" placeholder="e.g. Professor of Biology" v-model="recommenderTitle" />
      </b-form-group>

      <b-form-group label="Organization" label-for="organization" label-class="form-label">
        <b-form-input id="organization" placeholder="Enter organization" v-model="organization" />
      </b-form-group>

      <b-form-group label="Status" label-for="status" label-class="form-label">
        <v-select
          id="status"
          class="form-control v-select-custom"
          v-model="status"
          :options="statusOptions"
          placeholder="Select status"
        />
      </b-form-group>

      <b-form-group
        label="Upload Letter"
        label-for="document"
        label-class="form-label"
        class="row-align-items-center"
      >
        <b-progress :max="100" animated v-if="isUploading">
          <b-progress-bar :value="uploadPercent">
            <span>Uploading (<strong>{{ uploadPercent }} %</strong>)</span>
          </b-progress-bar>
        </b-progress>
        <span v-else-if="docUrl">
          <div class="row">
            <div class="col-md-10">
              <a
                href="#"
                @click.prevent="downloadFile({ fileUrl: docUrl, removeTimestamp: true })"
                v-b-popover.hover.top="'Download'"
                ><i class="fas fa-file text-primary"></i> {{ parseFileNameFromUrl(docUrl, true) }}
              </a>
            </div>
            <div class="text-right col-md-2">
              <a href="#" class="text-danger" @click.prevent="removeFile()"
                ><i class="fas fa-times-circle"></i> Remove
              </a>
            </div>
          </div>
        </span>
        <b-media class="align-items-center" vertical-align="center" v-else>
          <b-form-file
            placeholder="Select Document (max size: 10MB, only .pdf and image files allowed)"
            :browse-text="$t('generalMsgs.browse')"
            v-model="selectedUploadFile"
            @input="uploadFile()"
            :disabled="isUploading"
            accept=".pdf, image/*"
          />
        </b-media>
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : 'Add' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import axios from 'axios';
import { get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import { parseFileNameFromUrl } from '../../../common/utils';
import { required, requiredIf } from 'vuelidate/lib/validators';

export default {
  extends: Page,

  data() {
    return {
      title: 'Add Letter of Recommendation',
      isLoading: false,
      type: null,
      typeOther: '',
      recommenderName: '',
      recommenderTitle: '',
      organization: '',
      status: null,
      docUrl: '',
      isUploading: false,
      selectedUploadFile: null,
      uploadPercent: 0,
      recommendationTypes: [
        'Academic Recommendation Letter',
        'Professional Recommendation Letter',
        'Character Reference',
        'Other',
      ],
      statusOptions: ['Requested', 'Submitted', 'Received'],
    };
  },

  validations() {
    return {
      type: { required },
      recommenderName: { required },
      typeOther: { required: requiredIf(() => this.type === 'Other') },
    };
  },

  computed: {
    checklistContext() {
      const uniId = Number(this.$route.query.uniId);
      const programId = Number(this.$route.query.program_id);
      return {
        uniId: Number.isFinite(uniId) && uniId > 0 ? uniId : null,
        programId: Number.isFinite(programId) && programId > 0 ? programId : null,
      };
    },
  },

  methods: {
    ...mapActions('fileDownload', ['downloadFile']),
    ...mapActions('student', [
      'createLetterOfRecommendation',
      'getLetterOfRecommendation',
      'updateLetterOfRecommendation',
      'getStudentUploadPresignedUrl',
      'updateShowEarnedPointToast',
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    parseFileNameFromUrl,
    get,
    goBack() {
      this.$router.go(-1);
    },
    async uploadFile(uploadType = 'documents') {
      this.uploadCancelTokenSource = axios.CancelToken.source();
      this.isUploading = true;

      try {
        const urlResp = await this.getStudentUploadPresignedUrl({
          file_name: this.selectedUploadFile.name,
          content_type: this.selectedUploadFile.type,
          upload_type: uploadType,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.selectedUploadFile,
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource.token,
          },
        });
        this.docUrl = urlResp.upload_url.split('?')[0];
      } catch (error) {
        this.selectedUploadFile = null;
      }

      this.uploadCancelTokenSource = null;
      this.uploadPercent = 0;
      this.isUploading = false;
    },
    removeFile() {
      this.selectedUploadFile = null;
      this.docUrl = null;
    },
    async fetchExisting() {
      try {
        const res = await this.getLetterOfRecommendation(this.$route.params.id);
        this.type = res.data.type;
        this.typeOther = res.data.type_other;
        this.recommenderName = res.data.recommender_name;
        this.recommenderTitle = res.data.recommender_title;
        this.organization = res.data.organization;
        this.status = res.data.status;
        this.docUrl = res.data.recommendation_letter_url;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async onSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            type: this.type,
            type_other: this.typeOther,
            recommender_name: this.recommenderName,
            recommender_title: this.recommenderTitle,
            organization: this.organization,
            status: this.status,
            recommendation_letter_url: this.docUrl,
          };

          if (this.$route.params.id) {
            await this.updateLetterOfRecommendation({ id: this.$route.params.id, data });
            this.makeToast({ variant: 'success', msg: 'Letter of recommendation updated!' });
          } else {
            const createPayload = {
              ...data,
              ...(this.checklistContext.uniId && {
                entity_type: 'university',
                entity_id: this.checklistContext.uniId,
              }),
              ...(this.checklistContext.uniId && this.checklistContext.programId && {
                program_id: this.checklistContext.programId,
              }),
            };
            const resp = await this.createLetterOfRecommendation(createPayload);
            this.makeToast({ variant: 'success', msg: 'Letter of recommendation added!' });
            if (resp.data.data.points_earned > 0) {
              this.updateShowEarnedPointToast(resp.data.data.points_earned);
            } else {
              this.makeToast({
                variant: 'info',
                msg: 'Saved successfully. No checklist points awarded for this program requirement.',
              });
            }
          }
          setTimeout(() => {
            this.$router.push({ name: 'my-letters-of-recommendation' });
          }, 1000);
        } catch (err) {
          this.makeToast({
            variant: 'danger',
            msg: get(err, 'response.data.message') || get(err, 'response.data.error') || this.$t('generalMsgs.genErrorMsg'),
          });
        }
        this.isLoading = false;
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields.' });
      }
    },
  },

  async mounted() {
    if (this.$route.params.id) {
      this.fetchExisting();
    }
  },
};
</script>
